import * as moment from "moment";
import {EvChargingBillMessage, EvChargingQuarterState} from "../../api/EvChargingApi";

/**
 * An Irish MPRN is 11 digits, starting with 10.
 */
export const MPRN_LENGTH = 11;

export const ISO_DATE = "YYYY-MM-DD";

/**
 * The digits of an MPRN as typed or pasted, whatever the spacing, cut at its length.
 */
export function mprnDigits(input: string): string {
	return input.replace(/\D/g, "").slice(0, MPRN_LENGTH);
}

/**
 * Groups the digits 4-3-4, the way they are easiest to read against a bill digit by digit.
 */
export function formatMprn(digits: string): string {
	return [digits.slice(0, 4), digits.slice(4, 7), digits.slice(7, MPRN_LENGTH)]
		.filter(group => group.length > 0)
		.join(" ");
}

/**
 * Positions where two MPRNs of the same length differ, to point at the digit a typo changed.
 */
export function differingPositions(a: string, b: string): number[] {
	if (a.length !== b.length) return [];
	const positions: number[] = [];
	for (let i = 0; i < a.length; i++)
		if (a.charAt(i) !== b.charAt(i)) positions.push(i);
	return positions;
}

/**
 * Where the next bill of a meter starts: the day after the last one ended.
 */
export function nextPeriodFrom(periodTo: string): string {
	return moment(periodTo, ISO_DATE, true).add(1, "day").format(ISO_DATE);
}

/**
 * Days covered by a bill, both ends included as on the bill. Undefined until the period reads forwards.
 */
export function periodDays(periodFrom?: string, periodTo?: string): number | undefined {
	if (!periodFrom || !periodTo) return undefined;
	const days = moment(periodTo, ISO_DATE, true).diff(moment(periodFrom, ISO_DATE, true), "days") + 1;
	return days > 0 ? days : undefined;
}

export function messagesFor(field: string, messages: EvChargingBillMessage[]): EvChargingBillMessage[] {
	return messages.filter(message => message.field === field);
}

export type ParsedKwh =
	{ kind: "empty" } |
	{ kind: "invalid"; message: string } |
	{ kind: "ok"; value: number };

/**
 * Energy as typed from a bill. Irish bills print thousands with a comma ("1,200 kWh"), so commas and spaces are
 * dropped rather than read as a decimal point: reading "1,200" as 1.2 would claim a thousandth of the energy without
 * anything looking wrong. Anything else that is not a plain number with up to three decimals is refused.
 */
export function parseKwh(input: string): ParsedKwh {
	const text = input.replace(/[,\s]/g, "");
	if (!text) return {kind: "empty"};
	if (!/^\d+(\.\d{1,3})?$/.test(text))
		return {kind: "invalid", message: "Enter the units used as a number, e.g. 1200 or 1045.5 (up to three decimals)."};
	const value = parseFloat(text);
	if (value <= 0) return {kind: "invalid", message: "The units used must be more than 0."};
	return {kind: "ok", value};
}

export function formatKwh(kwh: number): string {
	return new Intl.NumberFormat("en-IE", {maximumFractionDigits: 3}).format(kwh);
}

/**
 * Credits are kept at full precision; one decimal is enough to read them, and the client has not decided rounding.
 */
export function formatCredits(credits: number): string {
	return new Intl.NumberFormat("en-IE", {maximumFractionDigits: 1}).format(credits);
}

export function quarterLabel(year: number, quarter: number): string {
	return `Q${quarter} ${year}`;
}

/**
 * "July 2026" for the "2026-07" a quarter's month comes as.
 */
export function monthLabel(month: string): string {
	return moment(month, "YYYY-MM", true).format("MMMM YYYY");
}

export function meterAndJobCountLabel(meterCount: number, jobCount: number): string {
	return `${meterCount} ${meterCount === 1 ? "meter" : "meters"} · ${jobCount} ${jobCount === 1 ? "job" : "jobs"}`;
}

export function formatDate(isoDate: string): string {
	return moment(isoDate, ISO_DATE, true).format("D MMM YYYY");
}

/**
 * "1 Apr – 30 Apr 2026", the year written once when both ends share it.
 */
export function formatPeriod(from: string, to: string): string {
	const start = moment(from, ISO_DATE, true);
	const end = moment(to, ISO_DATE, true);
	return `${start.format(start.year() === end.year() ? "D MMM" : "D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
}

/**
 * The client's own words for where a quarter stands.
 */
export function quarterStateLabel(state: EvChargingQuarterState): string {
	switch (state) {
		case "IN_PROGRESS":
			return "In progress";
		case "SUBMISSION_WINDOW_OPEN":
			return "Window active";
		case "SUBMISSION_WINDOW_CLOSED":
			return "Window closed";
		default:
			// a state the backend added since: shown raw rather than as nothing
			return state;
	}
}

export function lateBillsLabel(count: number): string {
	return count === 1 ? "1 late job" : `${count} late jobs`;
}
